import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { fournisseurService } from '../../api/services';
import { Fournisseur, Page } from '../../types';
import { formatMatriculeFiscal } from '../../utils/formatters';
import { DataTable, Column } from '../../components/common/DataTable';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';

export function FournisseurList() {
  const navigate = useNavigate();
  const [fournisseurs, setFournisseurs] = useState<Fournisseur[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage, size: 10 };
      if (search) params.search = search;
      const data: Page<Fournisseur> = await fournisseurService.getAll(params);
      setFournisseurs(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [currentPage, search]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await fournisseurService.delete(deleteId);
      toast.success('Fournisseur supprimé');
      load();
    } catch {
      toast.error('Suppression impossible');
    } finally {
      setDeleteId(null);
    }
  };

  const columns: Column<Fournisseur>[] = [
    {
      key: 'raisonSociale', header: 'Raison sociale', sortable: true,
      render: (f) => (
        <div>
          <div className="font-medium text-gray-900">{f.raisonSociale}</div>
          {f.ville && <div className="text-xs text-gray-500">{f.ville}</div>}
        </div>
      ),
    },
    {
      key: 'matriculeFiscal', header: 'Matricule fiscal',
      render: (f) => <span className="text-sm text-gray-600 font-mono">{formatMatriculeFiscal(f.matriculeFiscal)}</span>,
    },
    { key: 'telephone', header: 'Téléphone', render: (f) => <span className="text-sm text-gray-600">{f.telephone || '-'}</span> },
    { key: 'email', header: 'Email', render: (f) => <span className="text-sm text-gray-600">{f.email || '-'}</span> },
    {
      key: 'actif', header: 'Statut',
      render: (f) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          f.actif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
          {f.actif ? 'Actif' : 'Inactif'}
        </span>
      ),
    },
    {
      key: 'actions', header: 'Actions',
      render: (f) => (
        <div className="flex items-center gap-2">
          <button onClick={(e) => { e.stopPropagation(); navigate(`/fournisseurs/${f.id}/edit`); }}
            className="text-primary-600 hover:text-primary-800 p-1" title="Modifier">
            <PencilSquareIcon className="h-4 w-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setDeleteId(f.id); }}
            className="text-red-600 hover:text-red-800 p-1" title="Supprimer">
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Fournisseurs</h1>
          <p className="page-subtitle">{totalElements} fournisseur(s)</p>
        </div>
        <button onClick={() => navigate('/fournisseurs/new')} className="btn-primary">
          <PlusIcon className="h-5 w-5 mr-1" /> Nouveau fournisseur
        </button>
      </div>

      <div className="card">
        <form onSubmit={(e) => { e.preventDefault(); setCurrentPage(0); load(); }} className="flex gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input type="text" placeholder="Rechercher..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="input-field pl-10" />
          </div>
          <button type="submit" className="btn-primary">Rechercher</button>
        </form>
      </div>

      <DataTable
        columns={columns} data={fournisseurs}
        totalElements={totalElements} totalPages={totalPages} currentPage={currentPage}
        onPageChange={setCurrentPage} loading={loading} emptyMessage="Aucun fournisseur"
        onRowClick={(f) => navigate(`/fournisseurs/${f.id}`)}
      />

      <ConfirmDialog
        isOpen={deleteId !== null} title="Confirmer la suppression"
        message="Supprimer ce fournisseur ?" onConfirm={handleDelete} onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}

export default FournisseurList;
